import type { PartnerDto, UserDto } from "@/api/api.dto";
import PARTNER_API from "@/api/classes/partner/partner_api";
import USER_API from "@/api/classes/user";
import { useUser } from "@/stores/user/user";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import { createNotification } from "@/tools/notification";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { MARKET_PLACE_OPTIONS, MarketPlace } from "@/types/market-place";
import { showConfirm } from "@/tools/ConfirmationDialog";
import router from "@/router";

export const PARTNER_FIELD_BY_ENABLED_MARKETPLACE: Record<
  MarketPlace,
  keyof PartnerDto
> = {
  [MarketPlace.ChestniyZnak]: "chestniyZnakEnabled",
  [MarketPlace.Ozon]: "ozonEnabled",
  [MarketPlace.Wildberries]: "wildberriesEnabled",
  [MarketPlace.YandexMarket]: "yandexMarketEnabled",
};

export const usePartner = defineStore("partner", () => {
  const userStore = useUser();
  const partner = ref<PartnerDto | null>(null);
  const partnerBrands = ref<string[]>([]);
  const usersInPartner = ref<UserDto[]>([]);
  const statusLoadUsersInPartner = ref(enumStatusLoad.NOT_STARTED);
  const statusLoadPartner = ref(enumStatusLoad.NOT_STARTED);
  const changeImageStatus = ref(enumStatusLoad.NOT_STARTED);
  const statusLoadGetBrandsPartner = ref(enumStatusLoad.NOT_STARTED);
  const hasProducts = ref(false);
  const isSyncResiduesLoading = ref(false);
  const isSyncResidues2Loading = ref(false);
  const isSyncResidues3Loading = ref(false);
  const usersInPartnerById = ref<Map<UserDto["id"], UserDto>>(new Map());

  const isAnyMarketEnabled = computed(() => {
    return (
      partner.value?.wildberriesEnabled ||
      partner.value?.ozonEnabled ||
      partner.value?.yandexMarketEnabled
    );
  });

  const hasLeastOneIntegration = computed(() => {
    if (!partner.value) return false;

    return (
      partner.value.wildberriesEnabled ||
      partner.value.ozonEnabled ||
      partner.value.yandexMarketEnabled
    );
  });

  const optionsBrands = computed(() => {
    return partnerBrands.value.map((el) => {
      return {
        value: el,
        label: el,
      };
    });
  });

  const attachedMarketPlaces = computed(() =>
    MARKET_PLACE_OPTIONS.filter(
      (option) =>
        partner.value?.[PARTNER_FIELD_BY_ENABLED_MARKETPLACE[option.value]],
    ),
  );

  const checkPartnerHasProducts = async () => {
    if (!userStore.user || !userStore.user.partnerId) {
      return;
    }
    const response = await PARTNER_API.hasPartnerProducts();
    hasProducts.value = !!response?.data;
  };

  const getPartner = async () => {
    try {
      statusLoadPartner.value = enumStatusLoad.LOADING;

      if (!userStore.user || !userStore.user.partnerId) {
        console.log("partnerId in user is null for get partner");
        statusLoadPartner.value = enumStatusLoad.LOADED;
        return;
      }
      const response = await USER_API.getPartner();
      if (response) {
        partner.value = { ...response.data };

        await checkPartnerHasProducts();

        await checkTokensExpiration();

        statusLoadPartner.value = enumStatusLoad.LOADED;

        return response.data;
      }

      statusLoadPartner.value = enumStatusLoad.LOADED;

      return null;
    } catch (err) {
      statusLoadPartner.value = enumStatusLoad.ERROR;
      console.error("Error getPartner:", err);
    }
  };

  const checkTokensExpiration = async () => {
    if (!partner.value) return;
    const { wildberriesTokenExpired, ozonTokenExpired, yandexTokenExpired } =
      partner.value;

    if (wildberriesTokenExpired || ozonTokenExpired || yandexTokenExpired) {
      const markets = [];
      if (wildberriesTokenExpired) markets.push("Wildberries");
      if (ozonTokenExpired) markets.push("Ozon");
      if (yandexTokenExpired) markets.push("Yandex Market");
      showConfirm({
        title: "Токены истекли",
        content: `Токены истекли (${markets.join(", ")}). Обновите их в настройках партнера.`,
        okText: "Обновить",
        cancelText: "Отмена",
        onOk: async () => {
          await router.push({ name: "integrationList" });
        },
      });
    }
  };

  const getUsersInPartner = async () => {
    try {
      if (
        !userStore.user ||
        !userStore.user.partnerId ||
        !partner.value ||
        !partner.value.id
      ) {
        return;
      }

      usersInPartner.value.length = 0;
      statusLoadUsersInPartner.value = enumStatusLoad.LOADING;

      const response = await USER_API.getUsersInPartner(userStore.user.id);

      statusLoadUsersInPartner.value = enumStatusLoad.LOADED;
      usersInPartner.value = [...response.data];

      const map = new Map<UserDto["id"], UserDto>();

      [...usersInPartner.value, userStore.user].forEach((user) => {
        map.set(user.id, user);
      });
      usersInPartnerById.value = map;

      return null;
    } catch (err) {
      statusLoadUsersInPartner.value = enumStatusLoad.ERROR;
      console.error("Error getPartner:", err);
    }
  };

  const getBrandsPartner = async () => {
    try {
      statusLoadGetBrandsPartner.value = enumStatusLoad.LOADING;

      const response = await PARTNER_API.getBrandsPartner();

      if (response) {
        partnerBrands.value = [...response.data];
      }

      statusLoadGetBrandsPartner.value = enumStatusLoad.LOADED;

      return null;
    } catch (err) {
      statusLoadGetBrandsPartner.value = enumStatusLoad.ERROR;
      console.error(err);
    }
  };

  const partnerIsNull = () => {
    return !!partner.value;
  };

  const clearPartner = () => {
    partner.value = null;
  };

  const isSyncResiduesOn = computed(() => partner.value?.isSyncResiduesOn);
  const syncResidues2 = computed(() => partner.value?.syncResidues2);
  const isSyncResidues2Enable = computed(
    () => partner.value?.isSyncResidues2Enable,
  );
  const syncResidues3 = computed(() => partner.value?.syncResidues3);
  const isSyncResidues3Enable = computed(
    () => partner.value?.isSyncResidues3Enable,
  );

  function $reset() {
    partner.value = null;
    usersInPartner.value = [];
    statusLoadUsersInPartner.value = enumStatusLoad.NOT_STARTED;
  }

  const offSyncResidues = async () => {
    isSyncResiduesLoading.value = true;

    try {
      await PARTNER_API.setAllSyncResidues({
        syncResidues: false,
        isSyncResidues2Enable: false,
        isSyncResidues3Enable: false,
      });
      await getPartner();

      const messageTitle = "Baskit больше не управляет остатками FBS";

      createNotification("success", "Успешно", messageTitle);
    } catch (error) {
      createNotification(
        "error",
        "Ошибка",
        "Не удалось изменить управление остатками FBS. Попробуйте позже.",
      );
    }

    isSyncResiduesLoading.value = false;
  };

  const setSyncResidues = async (value: boolean) => {
    isSyncResiduesLoading.value = true;

    try {
      await PARTNER_API.setAllSyncResidues({ syncResidues: value });
      await getPartner();

      const messageTitle = value
        ? "Baskit взял под управление остатки FBS, данные будут синхронизированы в течение 5 минут"
        : "Baskit больше не управляет остатками FBS";

      createNotification("success", "Успешно", messageTitle);
    } catch (error) {
      createNotification(
        "error",
        "Ошибка",
        "Не удалось изменить управление остатками FBS. Попробуйте позже.",
      );
    }

    isSyncResiduesLoading.value = false;
  };

  const setSyncResidues2 = async (value: boolean) => {
    isSyncResidues2Loading.value = true;

    try {
      await PARTNER_API.setAllSyncResidues({
        isSyncResidues2Enable: value,
      });
      await getPartner();

      const messageTitle = value
        ? "Опция обнуления остатков FBS на МП при наличии FBM активирована!"
        : "Опция обнуления остатков FBS на МП при наличии FBM деактивирована!";

      createNotification("success", "Успешно", messageTitle);
    } catch (error) {
      createNotification(
        "error",
        "Ошибка",
        "Не удалось изменить состояние опции обнуления остатков FBS на МП при наличии FBM. Попробуйте позже.",
      );
    }

    isSyncResidues2Loading.value = false;
  };

  const setSyncResidues3 = async (value: boolean) => {
    isSyncResidues3Loading.value = true;

    try {
      await PARTNER_API.setAllSyncResidues({
        isSyncResidues3Enable: value,
      });
      await getPartner();

      const messageTitle = value
        ? "Опция поддержания остатков FBS на МП при наличии FBM активирована!"
        : "Опция поддержания остатков FBS на МП при наличии FBM деактивирована!";

      createNotification("success", "Успешно", messageTitle);
    } catch (error) {
      createNotification(
        "error",
        "Ошибка",
        "Не удалось изменить состояние опции поддержания остатков FBS на МП при наличии FBM. Попробуйте позже.",
      );
    }

    isSyncResidues3Loading.value = false;
  };

  const setSyncResidues2ThresholdRemain = async (value: number | null) => {
    isSyncResidues2Loading.value = true;

    try {
      await PARTNER_API.setAllSyncResidues({
        syncResidues2: value || 0,
      });
      await getPartner();

      const messageTitle = value
        ? `Пороговый остаток FBM установлен в ${value} шт.`
        : "Пороговый остаток FBM сброшен!";

      createNotification("success", "Успешно", messageTitle);
    } catch (error) {
      createNotification(
        "error",
        "Ошибка",
        "Не удалось изменить пороговый остаток FBM. Попробуйте позже.",
      );
    }

    isSyncResidues2Loading.value = false;
  };

  const setSyncResidues3IrreducibleRemain = async (value: number | null) => {
    isSyncResidues3Loading.value = true;

    try {
      await PARTNER_API.setAllSyncResidues({
        syncResidues3: value || 0,
      });
      await getPartner();

      const messageTitle = value
        ? `Пороговый остаток FBS установлен в ${value} шт.`
        : "Пороговый остаток FBS сброшен!";

      createNotification("success", "Успешно", messageTitle);
    } catch (error) {
      createNotification(
        "error",
        "Ошибка",
        "Не удалось изменить управление остатками FBS. Попробуйте позже.",
      );
    }

    isSyncResidues3Loading.value = false;
  };

  const changeImage = async (image: File | null) => {
    changeImageStatus.value = enumStatusLoad.LOADING;

    try {
      await PARTNER_API.changeImage(image);
      await getPartner();

      createNotification("success", "Успех", "Логотип успешно обновлен.");

      changeImageStatus.value = enumStatusLoad.LOADED;
    } catch (error) {
      createNotification("error", "Ошибка", "Не удалось обновить логотип.");

      changeImageStatus.value = enumStatusLoad.ERROR;
    }
  };

  return {
    partner,
    isAnyMarketEnabled,
    getPartner,
    partnerIsNull,
    clearPartner,
    hasLeastOneIntegration,
    hasProducts,
    checkPartnerHasProducts,
    usersInPartner,
    getUsersInPartner,
    statusLoadUsersInPartner,
    statusLoadPartner,
    getBrandsPartner,
    partnerBrands,
    statusLoadGetBrandsPartner,
    optionsBrands,
    $reset,
    isSyncResiduesOn,
    setSyncResidues,
    isSyncResiduesLoading,
    isSyncResidues2Loading,
    isSyncResidues3Loading,
    usersInPartnerById,
    changeImage,
    attachedMarketPlaces,
    changeImageStatus,
    syncResidues2,
    isSyncResidues2Enable,
    syncResidues3,
    isSyncResidues3Enable,
    setSyncResidues2,
    setSyncResidues3,
    setSyncResidues2ThresholdRemain,
    setSyncResidues3IrreducibleRemain,
    offSyncResidues,
  };
});
